import { Modules } from 'scbk-services/config';
import { EventBus, EventType } from 'scbk-services/event-bus/bus/event-bus';
import { RouterBackOptions, RouteType } from 'scbk-services/types/route/route';
import { deviceUtils } from 'scbk-services/utils';
import { create } from 'zustand';

const STORAGE_KEY = '__routerHistoryStack__';

/**
 * 라우터 히스토리 타입
 */
export enum RouterHistoryType {
    NONE = 'NONE',
    PUSH = 'PUSH',
    REPLACE = 'REPLACE',
    POP = 'POP',
    RESET = 'RESET',
}

/**
 *
 */
export interface RouterHistoryEntry<T = any> {
    module: string | Modules;
    id: string;
    path: string;
    state?: any;
    routeData?: T;
    routeType?: RouteType;
    query?: string | URLSearchParams;
}

interface RouteInfo {
    id: string;
    path: string;
}

/**
 *
 */
interface RouterHistoryState {
    storageKey: string;
    lastHistoryType: RouterHistoryType;
    stack: RouterHistoryEntry[];
    currentRoute: RouterHistoryEntry | undefined;
    previousRoute: RouteInfo | undefined;
    pushRoute: (entry: RouterHistoryEntry) => void;
    replaceRoute: (entry: RouterHistoryEntry) => void;
    popRoute: () => RouterHistoryEntry | null | undefined;
    peekRoute: (options?: RouterBackOptions) => RouterHistoryEntry | null | undefined;
    reset: (entry?: RouterHistoryEntry) => void;
    size: () => number;
    /** 세션 스토리지에서 기존 히스토리 로드 (새로고침 복구용) */
    hydrate: () => void;
    clear: () => void;
    updateHistoryType: (historyType: RouterHistoryType) => void;
    targetBackRoute: (backTargetId: string) => void; // 2026.02.06 권태민
    setStack: (stack: RouterHistoryEntry[]) => void;
}

/**
 *
 */
const useRouterHistory = create<RouterHistoryState>()((set, get) => ({
    storageKey: STORAGE_KEY,
    stack: [],
    lastHistoryType: RouterHistoryType.NONE,
    currentRoute: undefined,
    previousRoute: undefined,
    /**
     * 새로운 라우트를 스택에 추가하는 액션 (라우터 푸시)
     *
     * @param {string} path - 이동할 라우트 경로
     * @param {any} [state] - 라우트와 함께 전달할 상태 데이터 (선택적)
     */
    pushRoute: (entry: RouterHistoryEntry) => {
        set((state) => {
            const last = { ...state.stack[state.stack.length - 1] };
            if (last && last.id === entry.id && last.path === entry.path) {
                return state;
            }

            const newStack = [...state.stack, { ...entry }];

            EventBus.getInstance().emit(EventType.RoutePushAction, { ...entry });

            return {
                stack: newStack,
                lastHistoryType: RouterHistoryType.PUSH,
                currentRoute: entry,
                previousRoute: state.currentRoute,
            };
        });
    },

    /**
     * 현재 라우트를 새로운 라우트로 교체하는 액션 (라우터 교체)
     *
     * @param {string} path - 교체할 라우트 경로
     * @param {any} [state] - 라우트와 함께 전달할 상태 데이터 (선택적)
     */
    replaceRoute: (entry: RouterHistoryEntry) => {
        set((state) => {
            if (state.stack.length === 0) {
                return { stack: [{ ...entry }] };
            }

            const newStack = state.stack.slice(0, -1).concat({ ...entry });

            //이벤트 처리
            EventBus.getInstance().emit(EventType.RouteReplaceAction, { ...entry });

            return {
                stack: newStack,
                lastHistoryType: RouterHistoryType.REPLACE,
                currentRoute: entry,
                previousRoute: state.currentRoute,
            };
        });
    },

    /**
     * target이 되는 라우트까지 stack을 교체하는 액션
     *
     * @param {string} backTargetId - 해당 라우트까지만
     */
    targetBackRoute: (backTargetId: string, options?: RouterBackOptions) => {
        set((state) => {
            const idx = state.stack.findIndex((item) => item.id === backTargetId);

            if (idx === -1) return state;

            const newStack = state.stack.slice(0, idx + 1);
            const targetEntry = newStack[newStack.length - 1];

            // back 이동시 routeData 덮어쓰기
            const mergedEntry = {
                ...targetEntry,
                routeData: {
                    ...(targetEntry?.routeData ?? {}),
                    ...(options?.routeData ?? {}),
                },
            };

            //이벤트 처리
            //TODO: 이벤트 처리 상수 추가 필요
            EventBus.getInstance().emit(EventType.RouteReplaceAction, {
                ...mergedEntry,
            });

            //TODO: 이벤트 처리 상수 추가 필요
            return {
                stack: [...newStack.slice(0, -1), mergedEntry],
                lastHistoryType: RouterHistoryType.REPLACE,
                currentRoute: mergedEntry,
                previousRoute: state.currentRoute,
            };
        });
    },

    /**
     * 이전 라우트로 이동하는 액션 (뒤로가기)
     *
     * @returns {RouterStack | null} 이동한 라우트 정보 또는 null (이동할 수 없는 경우)
     */
    popRoute: () => {
        const current = [...get().stack];
        const newStack = current.slice(0, -1);
        const popped = current[current.length - 1];
        //이벤트 처리
        EventBus.getInstance().emit(EventType.RoutePopAction);

        set(() => {
            return {
                stack: newStack,
                lastHistoryType: RouterHistoryType.POP,
            };
        });
        return popped;
    },

    peekRoute: (options?: RouterBackOptions) => {
        const current = get().stack;
        const peek = current[current.length - 1];
        const currentRoute = get().currentRoute;

        if (!peek) return peek;

        // back 이동시 routeData 덮어쓰기
        const mergedPeekEntry = {
            ...peek,
            routeData: {
                ...(peek?.routeData ?? {}),
                ...(options?.routeData ?? {}),
            },
        };

        const newStack = [...current];
        newStack[newStack.length - 1] = mergedPeekEntry;

        if (peek && peek.id !== currentRoute?.id) {
            set((state) => {
                return { stack: newStack, currentRoute: peek, previousRoute: state.currentRoute };
            });
        } else {
            set((state) => {
                return {
                    stack: newStack,
                    currentRoute: mergedPeekEntry,
                    previousRoute: state.previousRoute,
                };
            });
        }

        return mergedPeekEntry;
    },

    /**
     * 메뉴이동을 하면서 router history reset 처리
     * @param entry
     * @returns
     */
    reset: (entry?: RouterHistoryEntry) => {
        if (!entry) {
            set({ stack: [] });
            return;
        }

        //이벤트 처리
        EventBus.getInstance().emit(EventType.RouteResetAction);

        //console.log('### useRouterHistory reset', entry);
        //console.log('### useRouterHistory reset', new Error(''));

        set((state) => {
            return {
                stack: [entry],
                lastHistoryType: RouterHistoryType.RESET,
                currentRoute: entry,
                previousRoute: state.currentRoute,
            };
        });
    },

    size: (): number => {
        const current = get().stack;
        return current.length;
    },

    /** 세션 스토리지에서 기존 히스토리 로드 (새로고침 복구용) */
    hydrate: () => {
        try {
            const stored = deviceUtils.isPC()
                ? localStorage.getItem(STORAGE_KEY)
                : sessionStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    set({ stack: parsed });
                }
            }
        } catch {
            set({ stack: [] });
        }
    },

    /** 스택 초기화 (로그아웃, 앱 재시작 등) */
    clear: () => {
        deviceUtils.isPC()
            ? localStorage.removeItem(STORAGE_KEY)
            : sessionStorage.removeItem(STORAGE_KEY);
        set({ stack: [] });
    },

    updateHistoryType: (historyType: RouterHistoryType) => {
        set({
            lastHistoryType: historyType,
        });
    },

    setStack: (stack: RouterHistoryEntry[]) => {
        set({
            stack,
        });
    },
}));

useRouterHistory.subscribe((state, _) => {
    deviceUtils.isPC()
        ? localStorage.setItem(STORAGE_KEY, JSON.stringify(state.stack))
        : sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state.stack));
});

export { useRouterHistory };
